import React, { useState, useEffect, useCallback } from 'react';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ReviewCard from '../../ReviewCard/Index';
import { fetchAllTestimonials } from '../../../Services/testimonialsServices';
import './Testimonials.css';

const NextArrow = ({ className, onClick, disabled }) => (
  <div
    className={`${className} testimonial-custom-arrow right ${
      disabled ? 'testimonial-arrow-disabled' : ''
    }`}
    onClick={disabled ? null : onClick}
  >
    <i
      className="fas fa-chevron-right"
      style={{
        fontSize: 24,
        color: disabled ? "#ccc" : "black",
        opacity: disabled ? 0.5 : 1
      }}
    ></i>
  </div>
);

const PrevArrow = ({ className, onClick, disabled }) => (
  <div
    className={`${className} testimonial-custom-arrow left ${
      disabled ? 'testimonial-arrow-disabled' : ''
    }`}
    onClick={disabled ? null : onClick}
  >
    <i
      className="fas fa-chevron-left"
      style={{
        fontSize: 24,
        color: disabled ? "#ccc" : "black",
        opacity: disabled ? 0.5 : 1
      }}
    ></i>
  </div>
);

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const testimonials = await fetchAllTestimonials();
      
      const formattedReviews = testimonials.map(review => ({
        name: review.ReviewerName || review.name,
        time: review.DateCreated || review.time,
        text: review.ReviewText || review.text,
        avatar: review.avatar,
        rating: review.Rating || 0
      }));
      
      setReviews(formattedReviews);
    } catch (err) {
      setError('Failed to load reviews');
      console.error('Error fetching testimonials:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleBeforeChange = useCallback(() => {
    setIsAnimating(true);
  }, []);

  const handleAfterChange = useCallback(() => {
    setIsAnimating(false);
  }, []);

  const settings = {
    dots: false,
    infinite: reviews.length > 4,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 2,
    arrows: true,
    nextArrow: <NextArrow disabled={isAnimating} />,
    prevArrow: <PrevArrow disabled={isAnimating} />,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    className: 'testimonial-slider',
    beforeChange: handleBeforeChange,
    afterChange: handleAfterChange,
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          infinite: reviews.length > 4,
          arrows: true
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 2,
          infinite: reviews.length > 3,
          arrows: true
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          infinite: reviews.length > 2,
          dots: false
        }
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: reviews.length > 1,
          dots: false
        }
      },
      {
        breakpoint: 360,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: reviews.length > 1,
          dots: false
        }
      }
    ]
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex justify-center items-center min-h-40">
          <div className="animate-pulse">Loading reviews...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex justify-center items-center min-h-40 text-red-500">
          {error}
        </div>
      </div>
    );
  }

  if (!reviews.length) {
    return (
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex justify-center items-center min-h-40">
          No reviews available
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%', overflow: 'hidden' }}>
      <div className="testimonials-section py-12">
        <div className="mx-auto max-w-7xl px-4">
          <div className="slider-container">
            <Slider {...settings}>
              {reviews.map((review, index) => (
                <div key={`review-${index}-${review.name}`} className="px-2">
                  <ReviewCard review={review} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;